import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  IconButton,
  Badge,
  Tooltip,
  Typography,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../api/config/axiosConfig";
import { getSocketUrl } from "../../../api/config/apiConfig";
import "./style.css";

const playChime = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(587.33, ctx.currentTime);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12);

    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  } catch (e) {}
};

const NotificationDrawer = ({ socket, rootSocket }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    const token = localStorage.getItem("shelLoginToken");
    if (!token) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/notifications?limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res?.data?.data) {
        const items = res.data.data.items || [];
        setNotifications(items);
        setUnreadCount(
          res.data.data.unreadCount ?? items.filter((n) => !n.isSettled).length
        );
      }
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error("Failed to fetch notifications:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  // Listen to live socket notifications from both chat socket and system socket
  useEffect(() => {
    const handleNewNotif = (notif) => {
      if (!notif) return;
      if (soundEnabled) playChime();
      setNotifications((prev) => [notif, ...prev.filter((n) => n.uuid !== notif.uuid)]);
      setUnreadCount((prev) => prev + 1);
    };

    const handleChatMessage = (raw) => {
      const msg = raw?.message || raw;
      if (!msg || !msg.uuid) return;
      const chatNotif = {
        uuid: "chat_" + msg.uuid,
        title: `New message from ${msg.senderName || "Surveyor"}`,
        body: msg.content || (msg.messageType === "IMAGE" ? "📷 Sent a photo" : "📄 Sent a document"),
        type: "CHAT",
        conversationId: msg.conversationId,
        senderName: msg.senderName,
        surveyId: msg.surveyId,
        isSettled: false,
        createdAt: new Date().toISOString(),
      };
      handleNewNotif(chatNotif);
    };

    if (socket) {
      socket.on("newMessage", handleChatMessage);
      socket.on("messageBroadcast", handleChatMessage);
    }

    if (rootSocket) {
      rootSocket.on("notification:new", handleNewNotif);
      rootSocket.on("notification", handleNewNotif);
    }

    return () => {
      if (socket) {
        socket.off("newMessage", handleChatMessage);
        socket.off("messageBroadcast", handleChatMessage);
      }
      if (rootSocket) {
        rootSocket.off("notification:new", handleNewNotif);
        rootSocket.off("notification", handleNewNotif);
      }
    };
  }, [socket, rootSocket, soundEnabled]);

  const markAsRead = async (uuid) => {
    setNotifications((prev) =>
      prev.map((n) => (n.uuid === uuid ? { ...n, isSettled: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await axiosInstance.patch(`/notifications/${uuid}/read`, {});
    } catch (e) {}
  };

  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isSettled: true })));
    setUnreadCount(0);

    try {
      await axiosInstance.patch(`/notifications/read-all`, {});
    } catch (e) {}
  };

  const clearAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);

    try {
      await axiosInstance.delete(`/notifications/clear-all`);
    } catch (e) {}
  };

  const deleteNotification = async (uuid, e) => {
    if (e) e.stopPropagation();
    setNotifications((prev) => prev.filter((n) => n.uuid !== uuid));
    if (!notifications.find((n) => n.uuid === uuid)?.isSettled) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    try {
      await axiosInstance.delete(`/notifications/${uuid}`);
    } catch (e) {}
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const filteredNotifications = notifications.filter((n) => {
    if (selectedCategory === "Messages") {
      return (n.type || "").toUpperCase().includes("CHAT") || (n.type || "").toUpperCase().includes("MESSAGE");
    }
    if (selectedCategory === "Surveys") {
      return (n.type || "").toUpperCase().includes("SURVEY");
    }
    if (selectedCategory === "Alerts") {
      return (n.type || "").toUpperCase().includes("ALERT") || (n.type || "").toUpperCase().includes("SYSTEM");
    }
    return true;
  });

  return (
    <>
      <Tooltip title="Notification Center" arrow>
        <IconButton
          className="header-notification-btn"
          onClick={() => setIsOpen(true)}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                animation: unreadCount > 0 ? "pulse 2s infinite" : "none",
                fontWeight: 700,
              },
            }}
          >
            <NotificationsRoundedIcon style={{ color: "#1e293b", fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 420 },
            display: "flex",
            flexDirection: "column",
            boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Drawer Header */}
        <div className="notif-drawer-header">
          <div className="notif-header-title-group">
            <div className="notif-header-icon-box">
              <NotificationsActiveRoundedIcon style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18, color: "#1e293b" }}>
                Notifications
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                {unreadCount > 0 ? `${unreadCount} unread alerts` : "All caught up"}
              </Typography>
            </div>
          </div>

          <div className="notif-header-actions">
            <Tooltip title={soundEnabled ? "Mute alert chime" : "Enable alert chime"}>
              <IconButton
                size="small"
                onClick={() => setSoundEnabled(!soundEnabled)}
                sx={{ color: soundEnabled ? "#4f46e5" : "#94a3b8" }}
              >
                {soundEnabled ? <VolumeUpRoundedIcon fontSize="small" /> : <VolumeOffRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <IconButton size="small" onClick={() => setIsOpen(false)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="notif-categories-bar">
          {["All", "Messages", "Surveys", "Alerts"].map((cat) => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              clickable
              onClick={() => setSelectedCategory(cat)}
              className={`notif-category-chip ${selectedCategory === cat ? "active" : ""}`}
            />
          ))}
          <div style={{ display: "flex", gap: "4px" }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllRoundedIcon fontSize="small" />}
                onClick={markAllAsRead}
                className="notif-mark-all-btn"
              >
                Read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                onClick={clearAllNotifications}
                className="notif-mark-all-btn"
                sx={{ color: "#ef4444" }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <Divider />

        {/* Notifications Stream */}
        <div className="notif-stream-container">
          {loading && notifications.length === 0 ? (
            <div className="notif-empty-state">
              <div className="notif-spinner"></div>
              <Typography variant="body2" color="textSecondary">
                Loading notifications...
              </Typography>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notif-empty-state">
              <div className="notif-empty-icon-box">
                <NotificationsRoundedIcon style={{ fontSize: 36, color: "#94a3b8" }} />
              </div>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                No notifications
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                You have no pending alerts in this category.
              </Typography>
            </div>
          ) : (
            filteredNotifications.map((n) => {
              const isChat =
                (n.type || "").toUpperCase().includes("CHAT") ||
                (n.type || "").toUpperCase().includes("MESSAGE");
              const isSurvey = (n.type || "").toUpperCase().includes("SURVEY");

              return (
                <div
                  key={n.uuid}
                  className={`notif-item-card ${n.isSettled ? "read" : "unread"}`}
                  onClick={() => {
                    markAsRead(n.uuid);
                    if (isChat) {
                      const meta = n.metadata || {};
                      navigate("/communication", {
                        state: {
                          conversationId: n.conversationId || meta.conversationId,
                          senderName: n.senderName || meta.senderName,
                          surveyId: n.surveyId || meta.surveyId,
                        },
                      });
                      setIsOpen(false);
                    } else if (isSurvey && (n.surveyId || n.metadata?.surveyId)) {
                      navigate(`/dashboard/view-details/${n.surveyId || n.metadata.surveyId}`);
                      setIsOpen(false);
                    }
                  }}
                >
                  <div
                    className={`notif-item-icon-box ${
                      isChat ? "chat" : isSurvey ? "survey" : "system"
                    }`}
                  >
                    {isChat ? (
                      <ChatBubbleOutlineRoundedIcon fontSize="small" />
                    ) : isSurvey ? (
                      <AssignmentOutlinedIcon fontSize="small" />
                    ) : (
                      <WarningAmberRoundedIcon fontSize="small" />
                    )}
                  </div>

                  <div className="notif-item-content">
                    <div className="notif-item-top">
                      <Typography className="notif-item-title">{n.title}</Typography>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Typography className="notif-item-time">
                          {formatTimeAgo(n.createdAt)}
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{ p: 0.2, color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                          onClick={(e) => deleteNotification(n.uuid, e)}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </div>
                    </div>

                    <Typography className="notif-item-body">{n.body}</Typography>

                    {isChat && (
                      <div className="notif-item-cta">
                        <span>Open in Communication Hub →</span>
                      </div>
                    )}
                  </div>

                  {!n.isSettled && <div className="notif-unread-dot"></div>}
                </div>
              );
            })
          )}
        </div>
      </Drawer>
    </>
  );
};

export default NotificationDrawer;
